import { z } from 'zod';

import { TranslatedTextSchema } from 'src/types/common';

import { ExaminationStatisticsSchema } from './statistics.types';
import {
  ExaminationSchema,
  ExaminationAttemptSchema,
  ExaminationQuestionSchema,
} from './examination.types';

// Schema for examination with questions (used in start examination response)
export const ExaminationWithQuestionsSchema = ExaminationSchema.extend({
  questions: z.array(ExaminationQuestionSchema),
  lessonId: z.number().optional(),
  lessonName: z.string().optional(),
  lessonType: z.string().optional(),
});

export const StartExaminationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  attempt: ExaminationAttemptSchema,
  examination: ExaminationWithQuestionsSchema,
});

const GivenAnswerSchema = z.object({
  id: z.number(),
  text: TranslatedTextSchema,
  isCorrect: z.boolean(),
});

const CorrectAnswerSchema = z.object({
  id: z.number(),
  text: TranslatedTextSchema,
});

const CurrentAnswerResultSchema = z.object({
  isCorrect: z.boolean(),
  questionId: z.number(),
  givenAnswers: z.array(GivenAnswerSchema),
  correctAnswers: z.array(CorrectAnswerSchema),
});

const ControlStatusSchema = z.object({
  currentWrongAnswers: z.number(),
  maxWrongAnswers: z.number(),
  score: z.number(),
  passed: z.boolean(),
});

const ExaminationProgressSchema = z.object({
  answeredQuestions: z.number(),
  totalQuestions: z.number(),
});

const AnswerResultDataSchema = z.object({
  type: z.enum(['regular', 'completion']),
  examinationCompleted: z.boolean(),
  controlStatus: ControlStatusSchema,
  currentAnswerResult: CurrentAnswerResultSchema,
  examinationProgress: ExaminationProgressSchema,
  certificateId: z.number().nullable().optional(),
  lessonSubscriptionId: z.number().nullable().optional(),
  certificateWarning: z.string().nullable().optional(),
  certificateDownloadUrl: z.string().nullable().optional(),
  certificatePending: z.boolean().nullable().optional(),
});

export const SubmitAnswerResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: AnswerResultDataSchema,
});

export const GetExaminationResponseSchema = ExaminationSchema;

export const ExaminationStatisticsResponseSchema = ExaminationStatisticsSchema;

export type StartExaminationResponse = z.infer<typeof StartExaminationResponseSchema>;
export type SubmitAnswerResponse = z.infer<typeof SubmitAnswerResponseSchema>;
export type GetExaminationResponse = z.infer<typeof GetExaminationResponseSchema>;
export type ExaminationStatisticsResponse = z.infer<typeof ExaminationStatisticsResponseSchema>;

// Additional type exports for the new schemas
export type GivenAnswer = z.infer<typeof GivenAnswerSchema>;
export type CorrectAnswer = z.infer<typeof CorrectAnswerSchema>;
export type CurrentAnswerResult = z.infer<typeof CurrentAnswerResultSchema>;
export type ControlStatus = z.infer<typeof ControlStatusSchema>;
export type ExaminationProgress = z.infer<typeof ExaminationProgressSchema>;
export type AnswerResultData = z.infer<typeof AnswerResultDataSchema>;
export type ExaminationWithQuestions = z.infer<typeof ExaminationWithQuestionsSchema>;

// Backward compatibility exports (deprecated, use the new ones above)
export type QuestionHistoryItem = CurrentAnswerResult; // Alias for backward compatibility
export type ExaminationState = 'SUCCESS' | 'FAILED' | 'ONGOING'; // Backward compatibility
